import { useMemo, useState } from 'react';
import { Flex, Form } from 'antd';
import { Button, PageHeader } from 'components';
import { useHistory, useParams, Switch, Route, useLocation, useRouteMatch, Redirect } from 'react-router-dom';
import { RoleName } from '@enmon/commons/lib/tools';
import { useUserInfo } from '@/hooks/useUser';
import { useOneMeterModel } from 'api/hooks/meter-models/useOneModel';
import { MODEL_FORM_ID } from './constants';
import { ModelDetailContext } from './store';
import { useIsMutating } from 'react-query';
import { MUTATION_KEYS } from 'utils/queryKeys';
import { MeterModelTabs } from '@/routes/reseller-data/routes/model-library/routes/models/routes/detail/components/Tabs';
import type { MeterModelFormValues } from './types';
import { MeterModelTab } from '@/routes/reseller-data/routes/model-library/const';
import { MeterModelOverviewTab } from '@/routes/reseller-data/routes/model-library/routes/models/routes/detail/route/overview/Content';

export const MeterModelDetailPage = () => {
  const { path, url } = useRouteMatch();
  const { id } = useParams<{ id: string }>();
  const modelId = id ? Number(id) : undefined;
  const [isFormChanged, setIsFormChanged] = useState(false);

  const location = useLocation();
  const history = useHistory();
  const isUpdatingModel = useIsMutating(MUTATION_KEYS.meterModels.updateOne()) > 0;

  const { role } = useUserInfo() ?? {};
  const isMagicRole = role?.name === RoleName.Magic;
  const isCreateFormMode = !modelId;

  // Form
  const [form] = Form.useForm<MeterModelFormValues>();

  // Data
  const { data: modelData } = useOneMeterModel(
    { modelId, populate: ['manufacturer', 'meter_addons'] },
    { enabled: !!modelId },
  );

  // Handlers
  const handleTabChange = (activeKey: string) => {
    history.push(`${url}/${activeKey}`);
  };

  // Context value
  const contextValue = useMemo(
    () => ({
      modelId,
      model: modelData,
    }),
    [modelId, modelData],
  );

  return (
    <ModelDetailContext.Provider value={contextValue}>
      <Flex vertical gap={8}>
        <PageHeader
          breadcrumbs={[
            { path: '/reseller-data/model-library/model', breadcrumbName: 'Model library' },
            { path: '', breadcrumbName: isCreateFormMode ? 'Create new model' : modelData?.name },
          ]}
          extra={
            isMagicRole && (
              <Button
                disabled={!isFormChanged}
                form={MODEL_FORM_ID}
                htmlType="submit"
                loading={isUpdatingModel}
                type="primary"
              >
                Save
              </Button>
            )
          }
          ghost={false}
          title={isCreateFormMode ? 'Create new model' : modelData?.name}
          onBack={() => history.push('/reseller-data/model-library/model')}
        />
        {isCreateFormMode ? (
          <MeterModelOverviewTab form={form} setIsFormChanged={setIsFormChanged} />
        ) : (
          <Switch>
            <Route exact path={path}>
              <Redirect to={{ pathname: `${url}/${MeterModelTab.Overview}`, state: location.state }} />
            </Route>
            <Route
              path={`${path}/:tab`}
              render={() => (
                <MeterModelTabs form={form} setIsFormChanged={setIsFormChanged} onTabChange={handleTabChange} />
              )}
            />
          </Switch>
        )}
      </Flex>
    </ModelDetailContext.Provider>
  );
};
